import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Heart, Calendar, Users, Clock, Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { BACKEND_CONFIG } from '../../utils/productionConfig';

const API_URL = BACKEND_CONFIG.apiUrl;

interface ModalConfig {
  enabled: boolean;
  frequencyDays: number;
  interestCount: number;
  showDelay: number;
}

export function NewInterestModalSettings() {
  const [config, setConfig] = useState<ModalConfig>({
    enabled: true,
    frequencyDays: 2,
    interestCount: 10,
    showDelay: 3000
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/new-interest-modal-config/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setConfig(response.data.data);
        setLastUpdated(response.data.data.updatedAt || '');
      }
    } catch (error) {
      // Use defaults if not found
      toast.info('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.put(
        `${API_URL}/new-interest-modal-config/config`,
        config,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success('Settings saved successfully!');
        setLastUpdated(new Date().toISOString());
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="size-8 animate-spin text-rose-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-rose-100 to-pink-100 rounded-xl">
            <Heart className="size-6 text-rose-600" />
          </div>
          <div>
            <CardTitle>New Interest Modal Settings</CardTitle>
            <CardDescription>
              Configure when and how users see new interest notifications
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Enable/Disable */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <Label className="text-base font-semibold">Enable New Interest Modal</Label>
            <p className="text-sm text-gray-600 mt-1">
              Show users a modal when they have new pending interests
            </p>
          </div>
          <Switch
            checked={config.enabled}
            onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
          />
        </div>

        {config.enabled && (
          <>
            {/* Frequency Days */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="size-4 text-blue-500" />
                Show Frequency (Days)
              </Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={config.frequencyDays}
                onChange={(e) => setConfig({ ...config, frequencyDays: parseInt(e.target.value) || 1 })}
              />
              <p className="text-sm text-gray-600">
                Show modal every {config.frequencyDays} day{config.frequencyDays !== 1 ? 's' : ''} when new interests are available
              </p>
            </div>

            {/* Interest Count */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Users className="size-4 text-purple-500" />
                Maximum Interests to Display
              </Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={config.interestCount}
                onChange={(e) => setConfig({ ...config, interestCount: parseInt(e.target.value) || 1 })}
              />
              <p className="text-sm text-gray-600">
                Display up to {config.interestCount} new interest{config.interestCount !== 1 ? 's' : ''} in the modal
              </p>
            </div>

            {/* Show Delay */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="size-4 text-green-500" />
                Show Delay (milliseconds)
              </Label>
              <Input
                type="number"
                min="0"
                max="10000"
                step="100"
                value={config.showDelay}
                onChange={(e) => setConfig({ ...config, showDelay: parseInt(e.target.value) || 0 })}
              />
              <p className="text-sm text-gray-600">
                Wait {config.showDelay / 1000} second{config.showDelay !== 1000 ? 's' : ''} after page load before showing modal
              </p>
            </div>

            {/* Preview Card */}
            <div className="p-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg border border-rose-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-white rounded-lg shadow-sm">
                  <Heart className="size-5 text-rose-500" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">Current Configuration</h4>
                  <div className="mt-2 space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="size-4 text-green-500" />
                      <span>Modal shows every {config.frequencyDays} day{config.frequencyDays !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="size-4 text-green-500" />
                      <span>Displays up to {config.interestCount} interests</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="size-4 text-green-500" />
                      <span>Shows {config.showDelay / 1000}s after page load</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertCircle className="size-4 text-amber-500" />
                      <span>Only shows if user has pending interests</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Last Updated */}
        {lastUpdated && (
          <div className="text-sm text-gray-500">
            Last updated: {new Date(lastUpdated).toLocaleString()}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={fetchConfig}
          disabled={loading || saving}
        >
          {loading ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
          Reset to Saved
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-rose-600 hover:bg-rose-700"
        >
          {saving ? (
            <>
              <Loader2 className="size-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="size-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
